// The Routines list's two filter axes — **Space, then Agent** — and the pure logic behind them.
//
// This is `task-filters.ts` applied to routines, deliberately down to the function names, because the two
// screens are the same screen over different rows: Ongoing Tasks and Routines both answer "what are my
// agents doing", both are reachable from the account, a Space and an agent, and both must behave
// identically once you are there. The three conventions carried over from `task-filters.ts` (and from
// `file-browser.ts` before it) each prevent a defect this screen would otherwise have:
//
//   1. **Options are DERIVED from the routines in scope**, never from the full Space/agent roster —
//      a menu must not contain a value that matches nothing.
//   2. **A control with nothing to choose between is not rendered at all.**
//   3. **The label states the current value**, so the row reads as an answer rather than a prompt.
//
// The state is ids, not names: names are display, and two Spaces can be renamed to the same thing.
//
// Mirrored 1:1 by `RoutineFilters.swift` and `RoutineFilters.kt`; all three unit-test the same fixtures.

/** A routine, as far as filtering is concerned. */
export interface FilterableRoutine {
  workspaceId?: string;
  agentId?: string;
}

/** Something a filter can offer: an id plus the name to show for it. */
export interface FilterOption {
  id: string;
  name: string;
}

/** Which Space and which agent the list is narrowed to. `''` on either axis means "all". */
export interface RoutineFilterState {
  workspaceId: string;
  agentId: string;
}

export const NO_ROUTINE_FILTERS: RoutineFilterState = { workspaceId: '', agentId: '' };

/** Whether anything is narrowed — drives the Clear affordance. */
export function hasActiveRoutineFilters(filters: RoutineFilterState): boolean {
  return !!filters.workspaceId || !!filters.agentId;
}

/**
 * The Spaces worth offering: those that actually own a routine here, in the roster's own order so the
 * picker reads like the sidebar rather than a shifting most-recent list. A Space the roster hasn't
 * resolved yet is skipped rather than shown by id.
 *
 * The site-level brief routine carries an empty `workspaceId` on purpose (it belongs to every Space), so
 * it contributes nothing here — it is never the reason a Space appears in the menu.
 */
export function spacesOf(routines: FilterableRoutine[], workspaces: FilterOption[]): FilterOption[] {
  const present = new Set(routines.map((routine) => routine.workspaceId).filter(Boolean));
  return workspaces.filter((workspace) => present.has(workspace.id));
}

/**
 * The id of the synthetic "No agent" option. Not a real agent id — the roster holds GUIDs — so it can
 * never collide with one, and `filterRoutines` reads it as "the ones with no agent at all".
 */
export const UNASSIGNED_AGENT_ID = '__unassigned__';

/**
 * The agents worth offering, on the same rule: only those with a routine in scope, in roster order —
 * plus a trailing **"No agent"** bucket when some routine in scope has none.
 *
 * That bucket exists because a routine's agent is *derived*, not owned: it is whoever last did work for
 * it (see the server's `routineEnrichment`), so "none yet" is a normal, permanent state for a routine
 * that has never created a task — the morning brief is exactly that. Without the bucket those routines
 * are the only rows on the screen that no filter value can name, and on a one-agent account they also
 * suppress the picker entirely (one option is not a choice), so the axis disappears while a real
 * distinction is visible in the list.
 *
 * It is derived on the same terms as everything else: offered only when such a routine is actually in
 * scope. When NO routine has a named agent the bucket is the only option, which is not a choice either —
 * it would just restate the list — so `offersAChoice` still (correctly) hides the picker.
 */
export function agentsOf(
  routines: FilterableRoutine[],
  agents: FilterOption[],
  unassignedName: string,
): FilterOption[] {
  const present = new Set(routines.map((routine) => routine.agentId).filter(Boolean));
  const named = agents.filter((agent) => present.has(agent.id));
  const anyUnassigned = routines.some((routine) => !routine.agentId);
  return anyUnassigned ? [...named, { id: UNASSIGNED_AGENT_ID, name: unassignedName }] : named;
}

/**
 * Whether a picker earns its place. One option is not a choice — it names the only thing there is, which
 * the list already says — so a control appears only once there are two things to pick between.
 */
export function offersAChoice(options: FilterOption[]): boolean {
  return options.length > 1;
}

/**
 * Narrow the list. An axis set to `''` does not narrow. Both axes are AND-ed.
 *
 * A routine with **no** `workspaceId` is site-level (the morning brief) and belongs to every Space, so it
 * survives a Space filter rather than being hidden by one — the same rule the server applies when it
 * merges the brief into a workspace-scoped page. A routine with no `agentId` has no agent to match and is
 * therefore dropped by an Agent filter on a named agent, which is the honest answer: "routines Maverick
 * runs" cannot include one whose agent we don't know. `UNASSIGNED_AGENT_ID` selects exactly those rows.
 */
export function filterRoutines<T extends FilterableRoutine>(routines: T[], filters: RoutineFilterState): T[] {
  return routines.filter((routine) => {
    const space = !filters.workspaceId || !routine.workspaceId || routine.workspaceId === filters.workspaceId;
    const agent = !filters.agentId
      ? true
      : filters.agentId === UNASSIGNED_AGENT_ID
      ? !routine.agentId
      : routine.agentId === filters.agentId;
    return space && agent;
  });
}

/**
 * Drop a selection that the routines in scope no longer support, so a filter can never strand the list on
 * an empty screen with no visible cause. Happens for real: narrow to an agent, then narrow to a Space that
 * agent has no routine in, and without this the screen goes blank while both pickers still read as set.
 */
export function clampRoutineFilters(
  filters: RoutineFilterState,
  spaces: FilterOption[],
  agents: FilterOption[],
): RoutineFilterState {
  return {
    workspaceId: spaces.some((space) => space.id === filters.workspaceId) ? filters.workspaceId : '',
    agentId: agents.some((agent) => agent.id === filters.agentId) ? filters.agentId : '',
  };
}
